import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import SideTooltip from '../../../components/SideTooltip'
// Section-режим общей панели инструментов — см. док в bbarSectionContext.ts.
import { BbarSectionMode } from './bbarSectionContext'

export interface PopoverOption {
	value: string
	label: string
	icon?: React.ReactNode
}

interface PopoverPosition {
	top: number
	left: number
	maxHeight: number
	openUpward: boolean
}

interface BottomBarPopoverProps {
	icon?: React.ReactNode
	label: string
	options?: PopoverOption[]
	value?: string
	onChange?: (value: string) => void
	active?: boolean
	onToggle?: () => void
	disabled?: boolean
	/** Пригашенный, но кликабельный вид: настройка сейчас неактуальна
	 *  (например вокал при инструментале), клик по ней меняет режим. */
	dimmed?: boolean
	className?: string
	tooltip?: string
	slider?: {
		min: number
		max: number
		step?: number
		value: number
		onChange: (value: number) => void
		unit?: string
	}
	/** Заголовок секции в section-режиме (имя настройки: «Формат», «Длительность»).
	 *  Вне section-режима не используется. Без него секция без заголовка. */
	sectionTitle?: string
	/** Произвольное содержимое поповера ВМЕСТО options/slider (панель
	 *  инструментов: сюда кладётся SettingsPillsRow в section-режиме). */
	panel?: React.ReactNode
	/** То же, что panel, но содержимое может закрыть окно само (выбор в своей панели — как выбор опции). */
	panelRender?: (close: () => void) => React.ReactNode
	/** Ширина поповера для позиционирования (дефолт 200). */
	width?: number
	/** Свой класс окна (оно порталом в body): студия «Сцена» всегда тёмная — красит окно тёмным при светлой теме. */
	popoverClassName?: string
}

function calcPosition(pillEl: HTMLElement, popoverW = 200): PopoverPosition {
	const rect = pillEl.getBoundingClientRect()
	const viewportH = window.innerHeight
	const viewportW = window.innerWidth
	const gap = 8

	const spaceAbove = rect.top - gap
	const spaceBelow = viewportH - rect.bottom - gap
	const openUpward = spaceAbove > spaceBelow || spaceBelow < 120
	const left = Math.min(rect.left, viewportW - popoverW - 8)

	if (openUpward) {
		return { top: rect.top - gap, left, maxHeight: spaceAbove, openUpward: true }
	} else {
		return { top: rect.bottom + gap, left, maxHeight: spaceBelow, openUpward: false }
	}
}

/** Своя панель, которая может закрыть окно сама (panelRender). */
function RenderPanel({ render, close }: { render: (close: () => void) => React.ReactNode; close: () => void }) {
	return <>{render(close)}</>
}

export default function BottomBarPopover({
	icon,
	label,
	options,
	value,
	onChange,
	active,
	onToggle,
	disabled,
	dimmed,
	className = '',
	tooltip,
	slider,
	sectionTitle,
	panel: panelNode,
	panelRender,
	width,
	popoverClassName = '',
}: BottomBarPopoverProps) {
	const asSection = useContext(BbarSectionMode)
	const [open, setOpen] = useState(false)
	const [visible, setVisible] = useState(false)
	const [pos, setPos] = useState<PopoverPosition | null>(null)
	const pillRef = useRef<HTMLButtonElement>(null)
	const popoverRef = useRef<HTMLDivElement>(null)
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

	const hasPanel = !!panelNode || !!panelRender
	const isToggle = !options?.length && !slider && !hasPanel
	const isActive = isToggle ? active : open
	const hasPopover = (options && options.length > 0) || !!slider || hasPanel

	const openPopover = useCallback(() => {
		if (!pillRef.current) return
		if (closeTimer.current) clearTimeout(closeTimer.current)
		setPos(calcPosition(pillRef.current, width))
		setOpen(true)
		requestAnimationFrame(() => setVisible(true))
	}, [width])

	const closePopover = useCallback(() => {
		setVisible(false)
		closeTimer.current = setTimeout(() => {
			setOpen(false)
			setPos(null)
		}, 180)
	}, [])
	const panel = panelRender ? <RenderPanel render={panelRender} close={closePopover} /> : panelNode

	useEffect(() => {
		if (!open) return
		function update() {
			if (pillRef.current) setPos(calcPosition(pillRef.current, width))
		}
		window.addEventListener('scroll', update, true)
		window.addEventListener('resize', update)
		return () => {
			window.removeEventListener('scroll', update, true)
			window.removeEventListener('resize', update)
		}
	}, [open, width])

	useEffect(() => {
		if (!open) return
		function handleClick(e: MouseEvent) {
			const target = e.target as Node
			const inPill = pillRef.current?.contains(target)
			const inPopover = popoverRef.current?.contains(target)
			// Клик в ОВЕРЛЕЕ ПОВЕРХ поповера (шторка стиля/голоса, drill-in
			// сабменю, тур) — НЕ «клик мимо»: закрытие поповера размонтировало бы
			// панель инструментов вместе с открытой из неё шторкой → выбор жанра
			// умирал не применившись (юзер-баг). Такие клики игнорируем.
			const inOverlayAbove = (target as HTMLElement).closest?.(
				'.sheet-backdrop, .gm-tools-submenu, .spotlight-tour',
			)
			if (!inPill && !inPopover && !inOverlayAbove) closePopover()
		}
		document.addEventListener('mousedown', handleClick, true)
		return () => document.removeEventListener('mousedown', handleClick, true)
	}, [open, closePopover])

	useEffect(() => {
		if (!open) return
		function handleKey(e: KeyboardEvent) {
			if (e.key === 'Escape') closePopover()
		}
		document.addEventListener('keydown', handleKey)
		return () => document.removeEventListener('keydown', handleKey)
	}, [open, closePopover])

	// ── Section-режим: плоская секция общей панели инструментов ──
	// Все хуки выше уже вызваны (Rules of Hooks), здесь только презентация.
	if (asSection) {
		if (isToggle) {
			return (
				<div
					className={`bbar-section bbar-section--toggle${disabled ? ' bbar-section--disabled' : ''}${dimmed ? ' bbar-section--dim' : ''}`}
				>
					<button
						type="button"
						className="bbar-section__toggle-row"
						onClick={() => onToggle?.()}
						disabled={disabled}
						aria-pressed={!!active}
					>
						{icon && (
							<span className="bbar-section__toggle-icon" aria-hidden="true">
								{icon}
							</span>
						)}
						<span className="bbar-section__toggle-label">{label}</span>
						<span className={`bbar-section__switch${active ? ' is-on' : ''}`} aria-hidden="true">
							<span className="bbar-section__knob" />
						</span>
					</button>
				</div>
			)
		}
		return (
			<div className={`bbar-section${disabled ? ' bbar-section--disabled' : ''}${dimmed ? ' bbar-section--dim' : ''}`}>
				{(sectionTitle || slider || panel) && (
					<div className={`bbar-section__head${slider ? ' bbar-section__head--slider' : ''}`}>
						{sectionTitle ?? label}
						{/* У слайдера значение и диапазон — в строке заголовка
						    (просьба владельца): секция ниже занята только треком. */}
						{slider && (
							<span className="bbar-section__head-meta">
								<span className="bbar-popover__slider-val">
									{slider.value}
									{slider.unit ?? ''}
								</span>
								<span className="bbar-popover__slider-range">
									{slider.min} — {slider.max}
								</span>
							</span>
						)}
					</div>
				)}
				{/* Своё содержимое (например пара слайдеров «начало/конец»):
				    в секции показываем так же, как в поповере, иначе настройка
				    превратилась бы в пустую коробку с одним заголовком. */}
				{panel}
				{options && options.length > 0 && (
					<div className="bbar-section__chips" role="listbox" aria-label={sectionTitle ?? label}>
						{options.map((opt) => (
							<button
								key={opt.value}
								type="button"
								role="option"
								aria-selected={opt.value === value}
								className={`bbar-section__chip${opt.value === value ? ' is-on' : ''}`}
								onClick={() => onChange?.(opt.value)}
								disabled={disabled}
							>
								{opt.icon && (
									<span className="bbar-section__chip-icon" aria-hidden="true">
										{opt.icon}
									</span>
								)}
								{opt.label}
							</button>
						))}
					</div>
				)}
				{slider && (
					<div className="bbar-section__slider">
						<input
							type="range"
							className="bbar-slider bbar-slider--full"
							min={slider.min}
							max={slider.max}
							step={slider.step ?? 1}
							value={slider.value}
							onChange={(e) => slider.onChange(Number(e.target.value))}
							disabled={disabled}
							// Заливка прогресса: без градиента трек невидим (база
							// .bbar-slider рассчитана на inline-фон). Переменные
							// цвета — от темы, см. .bbar-section__slider.
							style={{
								background: `linear-gradient(to right, var(--bbar-slider-fill) ${
									((slider.value - slider.min) / Math.max(1, slider.max - slider.min)) * 100
								}%, var(--bbar-slider-track) ${
									((slider.value - slider.min) / Math.max(1, slider.max - slider.min)) * 100
								}%)`,
							}}
						/>
					</div>
				)}
			</div>
		)
	}

	function handlePillClick() {
		if (disabled) return
		if (isToggle) {
			onToggle?.()
		} else if (open) {
			closePopover()
		} else {
			openPopover()
		}
	}

	function handleOptionClick(optValue: string) {
		onChange?.(optValue)
		closePopover()
	}

	const popoverEl =
		open && pos
			? createPortal(
					<div
						ref={popoverRef}
						className={`bbar-popover bbar-popover--portal ${visible ? 'bbar-popover--visible' : ''} ${popoverClassName}`}
						role="listbox"
						aria-label={label}
						style={{
							position: 'fixed',
							left: pos.left,
							...(pos.openUpward
								? { bottom: window.innerHeight - pos.top, top: 'auto' }
								: { top: pos.top }),
							// Панель инструментов выше обычного списка опций (секции).
							maxHeight: Math.min(pos.maxHeight, panel ? 480 : 320),
							...(width ? { width } : {}),
							zIndex: 9999,
						}}
					>
						{panel}
						{!panel &&
							options?.map((opt) => (
								<button
									key={opt.value}
									type="button"
									role="option"
									aria-selected={opt.value === value}
									className={`bbar-popover__option ${opt.value === value ? 'bbar-popover__option--active' : ''}`}
									onClick={() => handleOptionClick(opt.value)}
								>
									{opt.icon && (
										<span className="bbar-popover__option-icon" aria-hidden="true">
											{opt.icon}
										</span>
									)}
									<span className="bbar-popover__option-label">{opt.label}</span>
									{opt.value === value && (
										<span className="check-badge" aria-hidden="true" />
									)}
								</button>
							))}

						{!panel && slider && (
							<div className="bbar-popover__slider">
								<div className="bbar-popover__slider-row">
									<span className="bbar-popover__slider-val">
										{slider.value}
										{slider.unit ?? ''}
									</span>
									<span className="bbar-popover__slider-range">
										{slider.min} — {slider.max}
									</span>
								</div>
								<input
									type="range"
									className="bbar-slider bbar-slider--full"
									min={slider.min}
									max={slider.max}
									step={slider.step ?? 1}
									value={slider.value}
									onChange={(e) => slider.onChange(Number(e.target.value))}
								/>
							</div>
						)}
					</div>,
					document.body,
				)
			: null

	const pill = (
		<button
			ref={pillRef}
			type="button"
			className={[
				'bbar-pill',
				isActive ? 'bbar-pill--active' : '',
				disabled ? 'bbar-pill--disabled' : '',
				dimmed ? 'bbar-pill--dim' : '',
			].join(' ')}
			onClick={handlePillClick}
			aria-haspopup={hasPopover ? 'listbox' : undefined}
			aria-expanded={hasPopover ? open : undefined}
			disabled={disabled}
		>
			{icon && (
				<span className="bbar-pill__icon" aria-hidden="true">
					{icon}
				</span>
			)}
			<span className="bbar-pill__label">{label}</span>
			{hasPopover && (
				<span
					className={`bbar-pill__arrow ${open ? 'bbar-pill__arrow--up' : ''}`}
					aria-hidden="true"
				>
					<svg width="10" height="10" viewBox="0 0 10 10" fill="none">
						<path
							d="M2 3.5l3 3 3-3"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</span>
			)}
		</button>
	)

	return (
		<div className={`bbar-pill-wrap ${className}`}>
			{/* Tooltip only when popover is closed */}
			{tooltip && !open ? <SideTooltip text={tooltip}>{pill}</SideTooltip> : pill}
			{popoverEl}
		</div>
	)
}
